import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { FormInput } from './FormInput';
import { Button } from './Button';
import { adminApi, customerApi, projectApi } from '../services/api';
import { useCustomerInvite } from '../hooks/useCustomerInvite';
import { isVerifiedCustomer } from '../utils/customerVerification';
import type { Customer, Project } from '../types';

/**
 * A project's Beställare is optional until the agreement is sent (ClickUp
 * 869ff1egy). Three states block signing for different reasons and are shown
 * apart: no Beställare (add one), invited but not verified (wait), verified.
 */
export type ProjectCustomerState = 'missing' | 'waiting' | 'linked';

export function projectCustomerState(project: Pick<Project, 'customerId' | 'customerInvitation'>): ProjectCustomerState {
  if (project.customerId) return 'linked';
  return project.customerInvitation ? 'waiting' : 'missing';
}

const shortDate = (value: string, language: string) =>
  new Date(value).toLocaleDateString(language.startsWith('en') ? 'en-GB' : 'sv-SE', { day: 'numeric', month: 'short' });

/** "Beställare saknas" / "Väntar på BankID", for lists and the project header. */
export function ProjectCustomerStatusBadge({ state }: { state: ProjectCustomerState }) {
  const { t } = useTranslation();
  if (state === 'linked') return null;
  return (
    <span className={`project-customer-pill is-${state}`} data-testid={`project-customer-status-${state}`}>
      <i className={`feather-${state === 'missing' ? 'user-plus' : 'clock'}`} aria-hidden="true"></i>
      {t(`projectCustomer.status.${state}`)}
    </span>
  );
}

interface ReadinessProps {
  hasAssignment: boolean;
  hasPricing: boolean;
  onAdd: () => void;
}

/** "Klart för signering · N av 3" above the tabs while the Beställare is missing. */
export function ProjectCustomerReadiness({ hasAssignment, hasPricing, onAdd }: ReadinessProps) {
  const { t } = useTranslation();
  const steps = [
    { key: 'assignment', done: hasAssignment },
    { key: 'pricing', done: hasPricing },
    { key: 'customer', done: false }
  ];
  const done = steps.filter(step => step.done).length;
  return (
    <section className="project-customer-readiness" data-testid="project-customer-readiness" aria-labelledby="project-customer-readiness-title">
      <div className="project-customer-readiness-main">
        <p className="project-customer-kicker">{t('projectCustomer.readiness.kicker', { done, total: steps.length })}</p>
        <h2 id="project-customer-readiness-title" className="project-customer-title">{t('projectCustomer.readiness.title')}</h2>
        <div
          className="progress project-customer-progress"
          role="progressbar"
          aria-label={t('projectCustomer.readiness.kicker', { done, total: steps.length })}
          aria-valuemin={0}
          aria-valuemax={steps.length}
          aria-valuenow={done}
        >
          <div className="progress-bar" style={{ width: `${(done / steps.length) * 100}%` }}></div>
        </div>
        <ul className="project-customer-checklist">
          {steps.map(step => (
            <li key={step.key} className={step.done ? 'is-done' : 'is-open'}>
              <i className={`feather-${step.done ? 'check' : step.key === 'customer' ? 'user-plus' : 'circle'}`} aria-hidden="true"></i>
              <span>{t(`projectCustomer.readiness.${step.key}`)}</span>
              <span className="visually-hidden">
                {step.done ? t('projectCustomer.readiness.doneSr') : t('projectCustomer.readiness.openSr')}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <Button onClick={onAdd} data-testid="project-customer-add">
        <i className="feather-user-plus me-2" aria-hidden="true"></i>
        {t('projectCustomer.readiness.add')}
      </Button>
    </section>
  );
}

interface WaitingProps {
  project: Project;
  onChange: () => void;
  onReplace: () => void;
}

/**
 * Sends the Beställare's invitation again. Every place that waits for BankID
 * reminds the same way (ClickUp 869fn1nyr).
 */
export function useCustomerReminder(project: Project | null | undefined, onChange: () => void) {
  const { t } = useTranslation();
  const [notice, setNotice] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [sending, setSending] = useState(false);
  const invitationId = project?.customerInvitation?.id;

  const remind = async () => {
    if (!invitationId) return;
    setSending(true);
    setNotice(null);
    try {
      await customerApi.resendInvitation(invitationId);
      setNotice({ type: 'success', text: t('projectCustomer.waiting.reminderSent') });
      onChange();
    } catch {
      setNotice({ type: 'danger', text: t('projectCustomer.waiting.reminderError') });
    } finally {
      setSending(false);
    }
  };

  return { remind, sending, notice };
}

/** "Väntar på BankID" above the tabs: the Hantverkare waits, or reminds. */
export function ProjectCustomerWaiting({ project, onChange, onReplace }: WaitingProps) {
  const { t, i18n } = useTranslation();
  const invitation = project.customerInvitation;
  const { remind, sending, notice } = useCustomerReminder(project, onChange);
  if (!invitation) return null;
  const name = invitation.name || invitation.email;
  const date = shortDate(invitation.sentAt, i18n.language);

  return (
    <section className="project-customer-waiting" data-testid="project-customer-waiting" aria-labelledby="project-customer-waiting-title">
      <div className="project-customer-waiting-main">
        <p className="project-customer-kicker">{t('projectCustomer.status.waiting')}</p>
        <h2 id="project-customer-waiting-title" className="project-customer-title">{name}</h2>
        <p className="text-muted small mb-2">{t('projectCustomer.waiting.invited', { email: invitation.email, date })}</p>
        <p className="mb-3">{t('projectCustomer.waiting.text', { name })}</p>
        {notice && <div className={`alert alert-${notice.type} py-2 mb-3`} role="status">{notice.text}</div>}
        <div className="project-customer-actions">
          <Button variant="outline-primary" onClick={() => void remind()} isLoading={sending} data-testid="project-customer-remind">
            <i className="feather-send me-2" aria-hidden="true"></i>
            {t('projectCustomer.waiting.remind')}
          </Button>
          <Button variant="outline-primary" onClick={onReplace} data-testid="project-customer-replace">
            <i className="feather-repeat me-2" aria-hidden="true"></i>
            {t('projectCustomer.waiting.replace')}
          </Button>
        </div>
      </div>
      <ol className="project-customer-steps" aria-label={t('projectCustomer.waiting.stepsTitle')}>
        <li className="is-done">
          <i className="feather-check" aria-hidden="true"></i>
          <span>{t('projectCustomer.waiting.stepSent', { date })}</span>
        </li>
        <li className="is-current">
          <i className="feather-clock" aria-hidden="true"></i>
          <span>{t('projectCustomer.waiting.stepBankId', { name })}</span>
        </li>
        <li>
          <i className="feather-link" aria-hidden="true"></i>
          <span>{t('projectCustomer.waiting.stepLinked')}</span>
        </li>
      </ol>
    </section>
  );
}

interface SigningBlockProps {
  project: Project;
  onAdd: () => void;
  /** The project changed (a reminder was sent); offers "Skicka påminnelse" while waiting. */
  onChange?: () => void;
  /** A reminder shared with another button, so both show the same outcome. */
  reminder?: ReturnType<typeof useCustomerReminder>;
}

/**
 * Why "Signera och skicka" is off, always with the next step on the same spot
 * (ClickUp 869fn1nyr): add a Beställare, or remind the one who is invited.
 * Shared by the Kontrakt tab and the contract wizard.
 */
export function ProjectCustomerSigningBlock({ project, onAdd, onChange, reminder: sharedReminder }: SigningBlockProps) {
  const { t } = useTranslation();
  const ownReminder = useCustomerReminder(project, onChange ?? (() => undefined));
  const reminder = sharedReminder ?? ownReminder;
  const state = projectCustomerState(project);
  if (state === 'linked') return null;
  const name = project.customerInvitation?.name || project.customerInvitation?.email || '';
  return (
    <div className={`project-customer-block is-${state}`} role="note" data-testid="project-customer-signing-block">
      <i className={`feather-${state === 'missing' ? 'user-plus' : 'clock'} project-customer-block-icon`} aria-hidden="true"></i>
      <div className="project-customer-block-body">
        <p className="mb-0">
          <strong>{t('projectCustomer.blocked.title')}</strong>{' '}
          {state === 'missing' ? t('projectCustomer.blocked.missing') : t('projectCustomer.blocked.waiting', { name })}
        </p>
        {reminder.notice && (
          <p className={`mb-0 mt-1 small text-${reminder.notice.type}`} role="status">{reminder.notice.text}</p>
        )}
      </div>
      {state === 'missing' ? (
        <Button className="project-customer-block-action" onClick={onAdd} data-testid="project-customer-block-add" data-autofocus>
          <i className="feather-user-plus me-2" aria-hidden="true"></i>
          {t('projectCustomer.readiness.add')}
        </Button>
      ) : onChange && (
        <Button
          variant="outline-primary"
          className="project-customer-block-action"
          onClick={() => void reminder.remind()}
          isLoading={reminder.sending}
          data-testid="project-customer-block-remind"
          data-autofocus
        >
          <i className="feather-send me-2" aria-hidden="true"></i>
          {t('projectCustomer.waiting.remind')}
        </Button>
      )}
    </div>
  );
}

/** The text for a disabled wizard "Signera och skicka", or null when signing may go on. */
export function projectCustomerSigningReason(project: Project | null | undefined, t: (key: string, options?: Record<string, unknown>) => string): string | null {
  if (!project) return null;
  const state = projectCustomerState(project);
  if (state === 'linked') return null;
  const name = project.customerInvitation?.name || project.customerInvitation?.email || '';
  return `${t('projectCustomer.blocked.title')} ${state === 'missing' ? t('projectCustomer.blocked.missing') : t('projectCustomer.blocked.waiting', { name })}`;
}

/** Right-hand panel on the Kontrakt tab while the Beställare is missing. */
export function ProjectCustomerPanel({ onInvite, onPick }: { onInvite: () => void; onPick: () => void }) {
  const { t } = useTranslation();
  return (
    <aside className="project-customer-panel" data-testid="project-customer-panel" aria-labelledby="project-customer-panel-title">
      <div className="d-flex align-items-center gap-3">
        <span className="project-customer-avatar" aria-hidden="true"><i className="feather-user"></i></span>
        <div>
          <p className="project-customer-kicker mb-0">{t('projectCustomer.panel.kicker')}</p>
          <h3 id="project-customer-panel-title" className="project-customer-title mb-0">{t('projectCustomer.panel.empty')}</h3>
        </div>
      </div>
      <p className="text-muted mb-0">{t('projectCustomer.panel.text')}</p>
      <Button onClick={onInvite} data-testid="project-customer-panel-invite">{t('projectCustomer.panel.invite')}</Button>
      <Button variant="outline-primary" onClick={onPick} data-testid="project-customer-panel-pick">{t('projectCustomer.panel.pick')}</Button>
    </aside>
  );
}

interface CustomerChangeProps {
  project: Project;
  /** The project changed: the agreements were withdrawn. */
  onChange: () => void;
  /** Opens "Lägg till beställare" to choose the new Beställare. */
  onChangeCustomer: () => void;
}

const apiErrorCode = (error: unknown) =>
  (error as { response?: { data?: { code?: string } } })?.response?.data?.code;

/**
 * Changing a linked Beställare (ClickUp 869fmxjgt). While no agreement has
 * been sent it is a plain "Byt beställare". Once one has, the Beställare is
 * locked and the way forward is to withdraw the agreement first.
 */
export function ProjectCustomerChange({ project, onChange, onChangeCustomer }: CustomerChangeProps) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!project.customerId) return null;
  const name = project.customerName || t('projectCustomer.change.theCustomer');

  if (!project.customerLocked) {
    return (
      <Button variant="outline-primary" size="sm" className="project-customer-change-action" onClick={onChangeCustomer}
        data-testid="project-customer-change">
        <i className="feather-repeat me-2" aria-hidden="true"></i>
        {t('projectCustomer.change.change')}
      </Button>
    );
  }

  const release = async () => {
    setBusy(true);
    setError(null);
    try {
      await projectApi.releaseCustomer(project.id);
      setConfirming(false);
      onChange();
      onChangeCustomer();
    } catch (failure) {
      setError(t(apiErrorCode(failure) === 'PROJECT_CUSTOMER_SIGNED'
        ? 'projectCustomer.change.signedError'
        : 'projectCustomer.change.releaseError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="project-customer-locked" data-testid="project-customer-locked">
      <p className="mb-2 small">
        <i className="feather-lock me-2" aria-hidden="true"></i>
        {t('projectCustomer.change.locked', { name })}
      </p>
      <Button variant="outline-primary" size="sm" className="project-customer-change-action" onClick={() => setConfirming(true)}
        data-testid="project-customer-release">
        <i className="feather-corner-up-left me-2" aria-hidden="true"></i>
        {t('projectCustomer.change.release')}
      </Button>
      {confirming && (
        <Modal
          title={t('projectCustomer.change.confirmTitle')}
          onClose={() => { if (!busy) setConfirming(false); }}
          closeAriaLabel={t('common.close')}
          footer={
            <div className="modal-action-row">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirming(false)} disabled={busy}>
                {t('common.cancel')}
              </button>
              <Button variant="danger" onClick={() => void release()} isLoading={busy} data-testid="project-customer-release-confirm">
                {t('projectCustomer.change.confirm')}
              </Button>
            </div>
          }
        >
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          <ul className="project-customer-release-list mb-0">
            <li>{t('projectCustomer.change.effectDrafts', { name })}</li>
            <li>{t('projectCustomer.change.effectNotice', { name })}</li>
            <li>{t('projectCustomer.change.effectChange')}</li>
          </ul>
        </Modal>
      )}
    </div>
  );
}

interface AddCustomerModalProps {
  project: Project;
  companyName?: string | null;
  initialTab?: 'invite' | 'pick';
  /** Admin picks among the Hantverkare's customers; inviting is the Hantverkare's. */
  isAdmin?: boolean;
  onClose: () => void;
  /** The project changed: linked at once, or now waiting for BankID. */
  onDone: () => void;
  /** Above another dialog, such as the contract wizard. */
  zIndex?: number;
}

/**
 * "Lägg till beställare": invite a new Beställare with the same invitation
 * flow as Bjud in beställare (useCustomerInvite), or pick a verified one.
 */
export function AddProjectCustomerModal({ project, companyName, initialTab = 'invite', isAdmin = false, onClose, onDone, zIndex }: AddCustomerModalProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'invite' | 'pick'>(isAdmin ? 'pick' : initialTab);
  const invite = useCustomerInvite({ projectId: project.id, onInvited: onDone, onTimeout: onDone });
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [pickError, setPickError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tab !== 'pick' || customers) return;
    (isAdmin ? adminApi.getAllCustomers() : customerApi.getCustomers())
      .then(list => setCustomers(list.filter(customer => customer.companyNumber === project.companyNumber)))
      .catch(() => setCustomers([]));
  }, [tab, customers, isAdmin, project.companyNumber]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (customers ?? []).filter(customer =>
      !term || [customer.name, customer.email, String(customer.customerNumber)].some(value => (value ?? '').toLowerCase().includes(term))
    );
  }, [customers, search]);

  const pick = async () => {
    if (!selectedId) {
      setPickError(t('projectCustomer.modal.pickRequired'));
      return;
    }
    setSaving(true);
    setPickError(null);
    try {
      await projectApi.updateProject(project.id, {
        customerId: selectedId,
        description: project.description
      });
      onDone();
      onClose();
    } catch {
      setPickError(t('projectCustomer.modal.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const submitInvite = async () => {
    if (!invite.validate()) return;
    const outcome = await invite.send();
    if (outcome) onClose();
  };

  const kicker = t('projectCustomer.modal.kicker', { number: project.projectNumber, description: project.description });

  return (
    <Modal
      title={t('projectCustomer.modal.title')}
      renderHeader={titleId => (
        <div className="objection-header-main">
          <div className="objection-kicker">{kicker}</div>
          <div className="objection-header-title">
            <h2 className="modal-title" id={titleId}>{t('projectCustomer.modal.title')}</h2>
          </div>
        </div>
      )}
      headerClassName="objection-header"
      dialogClassName="objection-dialog project-customer-dialog"
      size="lg"
      fullscreenBelow="sm"
      zIndex={zIndex}
      onClose={onClose}
      closeAriaLabel={t('common.close')}
      footer={(
        <div className="modal-action-row">
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('projectCustomer.modal.cancel')}</button>
          {tab === 'invite' ? (
            <Button onClick={() => void submitInvite()} isLoading={invite.isInviting} data-testid="project-customer-send-invite">
              <i className="feather-send me-2" aria-hidden="true"></i>
              {t('projectCustomer.modal.sendInvite')}
            </Button>
          ) : (
            <Button onClick={() => void pick()} isLoading={saving} data-testid="project-customer-confirm-pick">
              {t('projectCustomer.modal.addToProject')}
            </Button>
          )}
        </div>
      )}
    >
      <div className="project-customer-tabs" role="tablist" aria-label={t('projectCustomer.modal.title')}>
        {(isAdmin ? (['pick'] as const) : (['invite', 'pick'] as const)).map(key => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`project-customer-tab${tab === key ? ' is-active' : ''}`}
            onClick={() => setTab(key)}
            data-testid={`project-customer-tab-${key}`}
          >
            {t(`projectCustomer.modal.tab.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'invite' ? (
        <div className="project-customer-tabpanel" role="tabpanel">
          {invite.error && <div className="alert alert-danger" role="alert">{invite.error}</div>}
          <FormInput
            id="project-customer-name"
            name="customerName"
            label={t('customerInvite.customerName')}
            value={invite.form.customerName}
            onChange={invite.handleChange}
            error={invite.errors.customerName ? t(invite.errors.customerName) : undefined}
            required
          />
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <FormInput
                name="customerEmail"
                type="email"
                label={t('customerInvite.customerEmail')}
                value={invite.form.customerEmail}
                onChange={invite.handleChange}
                error={invite.errors.customerEmail ? t(invite.errors.customerEmail) : undefined}
                required
              />
            </div>
            <div className="col-12 col-md-6">
              <FormInput
                type="tel"
                name="customerPhone"
                label={t('customerInvite.customerPhone')}
                value={invite.form.customerPhone}
                onChange={invite.handleChange}
                placeholder={t('customerInvite.customerPhonePlaceholder')}
                error={invite.errors.customerPhone ? t(invite.errors.customerPhone) : undefined}
              />
            </div>
          </div>
          <div className="project-customer-preview">
            <p className="project-customer-kicker">{t('projectCustomer.modal.previewTitle')}</p>
            <p className="mb-0">{t('projectCustomer.modal.preview', { company: companyName || t('projectCustomer.modal.yourCompany') })}</p>
          </div>
          <ol className="project-customer-invite-steps">
            {['step1', 'step2', 'step3'].map((key, index) => (
              <li key={key}>
                <span className="project-customer-step-number" aria-hidden="true">{index + 1}</span>
                {t(`projectCustomer.modal.${key}`)}
              </li>
            ))}
          </ol>
          {/* The same text for every invitation, never whether the address has an account (869fmx898). */}
          <p className="small text-muted mb-0" data-testid="invite-existing-account-hint">{t('customerInvite.existingAccountHint')}</p>
        </div>
      ) : (
        <div className="project-customer-tabpanel" role="tabpanel">
          <FormInput
            id="project-customer-search"
            name="search"
            label={t('projectCustomer.modal.search')}
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={t('projectCustomer.modal.searchPlaceholder')}
          />
          {customers === null ? (
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          ) : visible.length === 0 ? (
            <p className="text-muted mb-0">{t('projectCustomer.modal.none')}</p>
          ) : (
            <>
              <p className="text-muted small mb-2">{t('projectCustomer.modal.showing', { shown: visible.length, total: customers.length })}</p>
              <div className="project-customer-list" role="radiogroup" aria-label={t('projectCustomer.modal.search')}>
                {visible.map(customer => {
                  const verified = isVerifiedCustomer(customer);
                  return (
                    <label
                      key={customer.id}
                      className={`project-customer-option${selectedId === customer.id ? ' is-selected' : ''}${verified ? '' : ' is-disabled'}`}
                      data-testid={`project-customer-option-${customer.customerNumber}`}
                    >
                      <input
                        type="radio"
                        name="project-customer"
                        className="form-check-input"
                        value={customer.id}
                        checked={selectedId === customer.id}
                        disabled={!verified}
                        onChange={() => setSelectedId(customer.id)}
                      />
                      <span className="flex-grow-1 min-w-0">
                        <span className="d-block fw-semibold text-truncate">{customer.name}</span>
                        <span className="d-block small text-muted text-truncate">#{customer.customerNumber} · {customer.email}</span>
                      </span>
                      <span className={`badge ${verified ? 'bg-soft-success text-success' : 'bg-light text-muted'}`}>
                        {verified ? t('projectCustomer.modal.verified') : t('projectCustomer.modal.notVerified')}
                      </span>
                    </label>
                  );
                })}
              </div>
            </>
          )}
          {pickError && <div className="invalid-feedback d-block" role="alert">{pickError}</div>}
        </div>
      )}

      <p className="project-customer-lock-note">
        <i className="feather-lock me-2" aria-hidden="true"></i>
        {t('projectCustomer.modal.lockNote')}
      </p>
    </Modal>
  );
}
